/**
 * + More market helpers: labels, playability, and slip mapping.
 *
 * Client: only playable selections (gradeable today). Settlement grades
 * full-game h2h / spreads / totals; alternate_* maps onto those types.
 * Props / period markets are not shown until grading exists.
 */

import { formatOdds, formatPoint, formatTotalPoint } from "./odds";

const FEATURED = new Set(["h2h", "spreads", "totals"]);

/** Full-game alternate lines that settle like featured spreads/totals. */
const PLAYABLE_MAP = {
  alternate_spreads: "spreads",
  alternate_totals: "totals",
};

export const isFeaturedMarketKey = (key) => FEATURED.has(key);

export const getSlipMarketType = (marketKey) => {
  const key = String(marketKey || "");
  if (PLAYABLE_MAP[key]) return PLAYABLE_MAP[key];
  if (FEATURED.has(key)) return key;
  return null;
};

export const isExtraMarketPlayable = (marketKey) =>
  getSlipMarketType(marketKey) != null;

export const formatExtraMarketTitle = (key = "") =>
  String(key)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

/** Compact team label when a single-line string is required. */
export const shortTeamLabel = (name = "") => {
  const s = String(name || "").trim();
  if (!s) return "";
  if (s.length <= 18) return s;
  const parts = s.split(/\s+/).filter(Boolean);
  if (parts.length >= 2 && parts[0].length >= 3) {
    const two = `${parts[0]} ${parts[1]}`;
    return two.length <= 20 ? two : parts[0];
  }
  return s.slice(0, 16).trim();
};

/**
 * Structured chip parts for stacked UI: title (team / side), line, price.
 * Prefer this over a single truncated string so names stay readable.
 */
export const getOutcomeChipParts = (outcome = {}, marketKey = "") => {
  const name = String(outcome.name || "").trim();
  const description = String(outcome.description || "").trim();
  const point = outcome.point;
  const type = getSlipMarketType(marketKey) || marketKey;
  const priceRaw = outcome.price;
  const price =
    priceRaw !== undefined && priceRaw !== null && priceRaw !== ""
      ? formatOdds(priceRaw)
      : "";

  let title = "";
  let line = "";

  if (type === "totals" || String(marketKey).includes("totals")) {
    title = name || description;
    const totalLine = formatTotalPoint(point, name);
    line = totalLine !== "-" ? totalLine : "";
  } else if (type === "spreads" || String(marketKey).includes("spreads")) {
    title = description || name;
    if (point !== undefined && point !== null && point !== "") {
      line = formatPoint(point);
    }
  } else {
    title = description || name;
    if (point !== undefined && point !== null && point !== "") {
      line = formatPoint(point);
    } else if (name && name !== description) {
      line = name;
    }
  }

  const fullLabel =
    [title, line, price].filter(Boolean).join(" ").trim() || "—";

  return {
    title: title || "",
    line: line || "",
    price: price || "",
    fullLabel,
  };
};

export const formatOutcomeLabel = (outcome = {}, marketKey = "") =>
  getOutcomeChipParts(outcome, marketKey).fullLabel;

const outcomeTeamKey = (outcome = {}) =>
  String(outcome.name || outcome.description || "")
    .trim()
    .toLowerCase();

const byPointAsc = (a, b) => Number(a?.point) - Number(b?.point);

/**
 * Order outcomes for a readable chip grid: home (or Over) first, then away
 * (or Under), each sorted by line — single flowing grid, no two-column chrome.
 */
export const orderExtraOutcomesForDisplay = (
  outcomes = [],
  marketKey = "",
  game = null
) => {
  const list = Array.isArray(outcomes) ? [...outcomes] : [];
  if (!list.length) return list;

  const type = getSlipMarketType(marketKey) || marketKey;
  const isTotals =
    type === "totals" || String(marketKey).includes("totals");
  const isSpreads =
    type === "spreads" || String(marketKey).includes("spreads");

  if (isTotals) {
    const overs = [];
    const unders = [];
    const other = [];
    for (const o of list) {
      const side = String(o?.name || "").toLowerCase();
      if (side === "over") overs.push(o);
      else if (side === "under") unders.push(o);
      else other.push(o);
    }
    return [
      ...overs.sort(byPointAsc),
      ...unders.sort(byPointAsc),
      ...other.sort(byPointAsc),
    ];
  }

  if (isSpreads && game) {
    const homeKey = String(game.home_team || "").trim().toLowerCase();
    const awayKey = String(game.away_team || "").trim().toLowerCase();
    const home = [];
    const away = [];
    const other = [];
    for (const o of list) {
      const key = outcomeTeamKey(o);
      if (homeKey && key === homeKey) home.push(o);
      else if (awayKey && key === awayKey) away.push(o);
      else other.push(o);
    }
    return [
      ...home.sort(byPointAsc),
      ...away.sort(byPointAsc),
      ...other.sort(byPointAsc),
    ];
  }

  return list;
};

/**
 * Build the slip selection object for a playable extra-market outcome.
 * Returns null when the market cannot be placed yet.
 */
export const buildExtraMarketSlipItem = ({
  game,
  marketKey,
  outcome,
  checkId,
}) => {
  const slipType = getSlipMarketType(marketKey);
  if (!slipType || !game || !outcome) return null;

  const totalsSide = String(outcome.name || "").toLowerCase();
  let teamName = outcome.name;
  if (slipType === "totals") {
    teamName =
      totalsSide === "over"
        ? game.home_team
        : totalsSide === "under"
          ? game.away_team
          : outcome.description || outcome.name;
  } else if (slipType === "spreads") {
    teamName = outcome.name;
  } else if (slipType === "h2h") {
    teamName = outcome.name;
  }

  return {
    teamName,
    homeTeam: game.home_team,
    awayTeam: game.away_team,
    type: slipType,
    price: outcome.price ?? "-",
    point: outcome.point ?? "-",
    pointType: slipType === "totals" ? outcome.name : "",
    // Prefer whatever book the board card already uses; never show to player.
    bookMarkType: "",
    sport_key: game.sport_key,
    sport_title: game.sport_title,
    gameTime: game.commence_time,
    id: game.id,
    CheckId: checkId,
    marketKey,
    marketLabel: formatExtraMarketTitle(marketKey),
  };
};

/** Only groups that can hold playable extras this round. */
export const EXTRA_MARKET_GROUP_ORDER = ["alternates"];

export const EXTRA_MARKET_GROUP_LABELS = {
  alternates: "Alternate lines",
};
